/* ==========================================================================
   OUTLYRS CLUB — theme.js
   Global behaviour: chrome heights, sticky/transparent header, announcement
   rotation, mobile menu (focus trap, escape, scroll lock).
   Vanilla JS, no dependencies. Loaded with `defer`.
   ========================================================================== */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Chrome height custom properties ---------- */
  function setChromeHeights() {
    var announcement = document.querySelector(".announcement");
    var header = document.querySelector(".header");
    var root = document.documentElement;
    if (announcement) root.style.setProperty("--announcement-height", announcement.offsetHeight + "px");
    if (header) root.style.setProperty("--header-height", header.offsetHeight + "px");
  }

  /* ---------- Header: transparent -> solid on scroll ---------- */
  function initHeaderScroll() {
    var outer = document.querySelector(".header-outer");
    if (!outer) return;
    var behavior = outer.getAttribute("data-behavior");
    if (behavior !== "transparent") return;

    var threshold = 40;
    function onScroll() {
      outer.classList.toggle("is-solid", window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Announcement rotation ---------- */
  function initAnnouncement() {
    var root = document.querySelector("[data-announcement]");
    if (!root) return;
    var items = Array.prototype.slice.call(root.querySelectorAll(".announcement__item"));
    if (items.length < 2) return;

    var speed = parseInt(root.getAttribute("data-speed"), 10) || 5;
    var index = 0;
    var timer = null;

    function show(i) {
      items.forEach(function (el, n) { el.setAttribute("aria-hidden", n === i ? "false" : "true"); });
    }
    function next() {
      index = (index + 1) % items.length;
      show(index);
    }
    function start() {
      if (reduceMotion || timer) return;
      timer = window.setInterval(next, speed * 1000);
    }
    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    show(0);
    start();
    root.addEventListener("mouseenter", stop);
    root.addEventListener("mouseleave", start);
    root.addEventListener("focusin", stop);
    root.addEventListener("focusout", start);
  }

  /* ---------- Focus trap helper ---------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var nodes = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    var toggle = document.querySelector("[data-mobile-open]");
    var menu = document.getElementById("mobile-menu");
    if (!toggle || !menu) return;
    var closeBtn = menu.querySelector("[data-mobile-close]");
    var backdrop = document.querySelector("[data-mobile-backdrop]");
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      menu.classList.add("is-open");
      if (backdrop) backdrop.classList.add("is-open");
      menu.setAttribute("aria-hidden", "false");
      toggle.setAttribute("aria-expanded", "true");
      document.body.classList.add("is-locked");
      var target = menu.querySelector(FOCUSABLE);
      if (target) target.focus();
      document.addEventListener("keydown", onKeydown);
    }
    function close() {
      menu.classList.remove("is-open");
      if (backdrop) backdrop.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      toggle.setAttribute("aria-expanded", "false");
      document.body.classList.remove("is-locked");
      document.removeEventListener("keydown", onKeydown);
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    }
    function onKeydown(e) {
      if (e.key === "Escape") { close(); return; }
      if (e.key === "Tab") trapFocus(menu, e);
    }

    toggle.addEventListener("click", open);
    if (closeBtn) closeBtn.addEventListener("click", close);
    if (backdrop) backdrop.addEventListener("click", close);
    menu.querySelectorAll("a").forEach(function (a) { a.addEventListener("click", close); });
  }

  /* ---------- Localization auto-submit ---------- */
  function initLocalization() {
    document.querySelectorAll("[data-localization-submit]").forEach(function (select) {
      select.addEventListener("change", function () {
        if (select.form) select.form.submit();
      });
    });
  }

  /* ---------- Dialogs (<dialog> open/close triggers) ---------- */
  function initDialogs() {
    document.querySelectorAll("[data-dialog-open]").forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        var dialog = document.getElementById(trigger.getAttribute("data-dialog-open"));
        if (dialog && typeof dialog.showModal === "function") dialog.showModal();
        else if (dialog) dialog.setAttribute("open", "");
      });
    });
    document.querySelectorAll("[data-dialog-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var dialog = btn.closest("dialog");
        if (dialog) dialog.close();
      });
    });
  }

  /* ---------- Sticky mobile add-to-cart ---------- */
  function initStickyAtc() {
    var bar = document.querySelector("[data-sticky-atc]");
    var anchor = document.querySelector("[data-atc-anchor]");
    if (!bar || !anchor || !("IntersectionObserver" in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle("is-visible", !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    observer.observe(anchor);

    // Sticky "Buy it now" proxies the real express-checkout button (a form can
    // only safely render one dynamic checkout button).
    var stickyBuy = bar.querySelector("[data-sticky-buynow]");
    if (stickyBuy) {
      stickyBuy.addEventListener("click", function () {
        var real = document.querySelector(".product__buynow .shopify-payment-button__button");
        if (real) {
          real.click();
        } else {
          var wrap = document.querySelector(".product__buynow");
          if (wrap && wrap.scrollIntoView) wrap.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  }

  /* ---------- Quantity steppers ---------- */
  function initQuantity() {
    document.querySelectorAll(".qty").forEach(function (group) {
      var input = group.querySelector("input[type='number']");
      if (!input) return;
      var minus = group.querySelector("[data-qty-minus]");
      var plus = group.querySelector("[data-qty-plus]");
      function step(delta) {
        var min = parseInt(input.min, 10) || 1;
        var val = (parseInt(input.value, 10) || min) + delta;
        if (val < min) val = min;
        input.value = val;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (minus) minus.addEventListener("click", function () { step(-1); });
      if (plus) plus.addEventListener("click", function () { step(1); });
    });
  }

  /* ---------- Init ---------- */
  function init() {
    setChromeHeights();
    initHeaderScroll();
    initAnnouncement();
    initMobileMenu();
    initLocalization();
    initDialogs();
    initStickyAtc();
    initQuantity();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  window.addEventListener("resize", setChromeHeights, { passive: true });

  /* Expose a tiny namespace for later phases (cart drawer, search) */
  window.Outlyrs = window.Outlyrs || {};
  window.Outlyrs.trapFocus = trapFocus;
  window.Outlyrs.FOCUSABLE = FOCUSABLE;
})();
